import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import {Dispatch, SetStateAction, useCallback, useMemo, useState} from 'react';
import {mapObject} from 'underscore';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import useSimpleEvaluation from 'notebook/simple-tables/useSimpleEvaluation';
import {UpdateProcedureRunParams} from 'procedures/network/ProcedureRunDAO';
import buildFullExperimentPlan from 'procedures/planning/buildFullExperimentPlan';
import {
  ExperimentalInputEvaluatedValues,
  ExperimentalInputs,
  ExperimentalInputsComponentName,
  ExperimentalInputsContextProperties,
  ExperimentalInputsPerMethod,
} from 'procedures/planning/ProcedurePlanTypes';
import {
  convertExperimentalInputsToTableMap,
  convertOutputTableMapToEvaluatedValues,
} from 'procedures/planning/useInputTable';
import {extractMaterialFromTypedValue} from 'procedures/tables/procedureTableAdapterUtils';
import {CreateStudyModalOptionalParams} from 'studies/sidepanel/NewStudyDropdown';
import {ProcedureInformation, ProcedureRunDataEntryMode} from 'studies/StudyDAO';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';

import {
  ExperimentFlowchart,
  ExperimentProcedure,
  prepareInitialExperimentalInputs,
  selectFlowchartVersion,
  useExperimentProcedure,
} from './ExperimentalInputs';
import {serializeExperimentConditionsForNetwork} from './ExperimentConditionsNetworkTranslators';
import {
  areProcedureRunInputsEvaluatedValuesValid,
  validateConditions,
} from './procedureRunStudyValidationUtils';
import {MethodExecutionLimitState, useMethodExecutionLimitState} from './useMethodExecutionLimitState';

export interface ConditionValidationState {
  isDuplicateName: boolean;
  isNameLengthOverLimit: boolean;
  isDescLengthOverLimit: boolean;
}

/**
 * Shared metadata for a single experimental condition as it appears in the study design UI:
 * its name, description, replicate count, and current client-side validation state. This is the
 * common shape used by both run-level conditions ({@link Condition}) and plan-level condition
 * groups ({@link ConditionGroup}), so that hooks and components that only need to read or edit
 * these UI-facing fields can be generic over either representation.
 */
export interface BaseCondition {
  numReplicates: number;
  name: string;
  description?: string;
  validationState?: ConditionValidationState;
}

/**
 * A single experimental condition on a `ProcedureRun`. Backed by exactly one
 * `ProcedureRunCondition` row, identified by `id`. `id` is omitted for conditions that have been
 * created in the UI but not yet persisted.
 */
export interface Condition extends BaseCondition {
  id?: string;
}

/**
 * A single experimental condition as displayed for a `ProcedureRunPlan`. Logically one row in
 * the UI, but may map to multiple per-PMDV `ProcedureRunPlanCondition` rows in the database
 * under the per-PMDV plan model. `conditionIdsByPmdvId` maps each PMDV id to the id of its corresponding
 * `ProcedureRunPlanCondition`.
 */
export interface ConditionGroup extends BaseCondition {
  conditionIdsByPmdvId: Record<string, string>;
}

/**
 * Type guard that narrows a {@link BaseCondition} to a {@link ConditionGroup} (plan-level).
 * Discriminates on the presence of the `conditionIdsByPmdvId` map, which is only defined on `ConditionGroup`.
 */
export function isConditionGroup(condition: BaseCondition): condition is ConditionGroup {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'conditionIdsByPmdvId' in condition;
}

/**
 * Type guard that narrows a {@link BaseCondition} to a {@link Condition} (run-level).
 * Returns true for any condition that is not a {@link ConditionGroup}, since `Condition`'s `id`
 * field is optional and so cannot be used to discriminate on its own.
 */
export function isCondition(condition: BaseCondition): condition is Condition {
  return !isConditionGroup(condition);
}

/**
 * Builds the initial single-condition default for a run-level study design.
 * Used as the `createDefaultConditions` factory for {@link useConditionsAndExperimentalInputs} when
 * `TCondition` is {@link Condition}.
 */
export function createDefaultRunConditions(procedureType: ProcedureType): Array<Condition> {
  return [
    {
      numReplicates: 1,
      name: `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} 1`,
    },
  ];
}

/**
 * Builds the initial single-condition default for a plan-level study design.
 * Used as the `createDefaultConditions` factory for {@link useConditionsAndExperimentalInputs} when
 * `TCondition` is {@link ConditionGroup}. The returned group has an empty `conditionIdsByPmdvId` map,
 * matching the unpersisted-plan-condition convention used elsewhere (see
 * `planConditionGroupsButtonVariant` and `getStudyDesignFromProcedureRunPlan`).
 */
export function createDefaultPlanConditions(procedureType: ProcedureType): Array<ConditionGroup> {
  return [
    {
      numReplicates: 1,
      name: `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} 1`,
      conditionIdsByPmdvId: {},
    },
  ];
}

export interface ExperimentDesignState<TCondition extends BaseCondition = Condition> {
  /** User-selected procedure */
  procedureId: string | null;
  /** Setter for {@link procedureId} */
  setProcedureId: Dispatch<SetStateAction<string | null>>;
  /** Data for  {@link procedureId}. `null` iff. procedureId is null or data is still loading. */
  procedure: ExperimentProcedure | null;
  /** Metadata for user-defined conditions. Condition values are stored in experimentalInputs. */
  conditions: Array<TCondition> | null;
  /** Setter for {@link conditions} */
  setConditions: (conditions: Array<TCondition> | null, prevIndexes: Array<number | null>) => void;
  /** User-specified condition data. `null` iff. {@link procedure} is null or {@link numConditions} is null. */
  experimentalInputs: ExperimentalInputs | null;
  /** Setter for {@link experimentalInputs}. */
  setExperimentalInputs: Dispatch<SetStateAction<ExperimentalInputs>>;
  /** User-selected method. `null` iff. {@link procedure} is null or has no methods. */
  selectedMethodId: string | null;
  /** Setter for {@link selectedMethodId} */
  setSelectedMethodId: Dispatch<SetStateAction<string>>;
  /** Data for ExperimentalInputsContext react context. `null` iff. any of replicate, conditions, procedure are unspecified. */
  context: ExperimentalInputsContextProperties | null;
  /** User-specified procedure run description */
  description: string;
  /** Setter for {@link description} */
  setDescription: Dispatch<SetStateAction<string>>;
  /** User-specified data entry mode. If the study type does not support data entry mode selection, */
  dataEntryMode: ProcedureRunDataEntryMode;
  /** Setter for {@link dataEntryMode}. Unused if the study type does not support data entry mode selection. */
  setDataEntryMode: Dispatch<SetStateAction<ProcedureRunDataEntryMode>>;

  experimentParam: ProcedureInformation | null; // TODO: we need validation information here too...
  methodExecutionLimitState: MethodExecutionLimitState;
}

/**
 * Hook to validate state and produce a ProcedureInformation that can be used in a CreateStudyParam.
 *
 * @package exported for testing
 */
export function useExperimentParam(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null,
  description: string,
  experimentalInputs: ExperimentalInputs | null,
  evaluatedValues: ExperimentalInputEvaluatedValues | null,
  dataEntryMode: ProcedureRunDataEntryMode
): ProcedureInformation | null {
  return useMemo(() => {
    if (
      procedure == null ||
      experimentalInputs == null ||
      evaluatedValues == null ||
      conditions == null ||
      !validateConditions(conditions) ||
      !areProcedureRunInputsEvaluatedValuesValid(evaluatedValues)
    ) {
      return null;
    }
    const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
    const namesPerCondition = conditions.map((condition) => condition.name);
    const descriptionsPerCondition = conditions.map((condition) => condition.description);
    const experimentPlan = buildFullExperimentPlan({
      replicatesPerCondition,
      namesPerCondition,
      descriptionsPerCondition,
      numConditions: replicatesPerCondition.length,
      experimentalInputs,
      evaluatedValues,
    });
    // Backend requires exactly one of [templateProcedureFlowchartVersionId, workflowFlowchartConfigVersionId]
    const {versionId, isPoW} = selectFlowchartVersion(procedure);
    return Object.assign(
      {
        experimentConditions: serializeExperimentConditionsForNetwork(experimentPlan),
        confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
        description,
        dataEntryMode,
      },
      isPoW ? {workflowFlowchartConfigVersionId: versionId} : {templateProcedureFlowchartVersionId: versionId}
    );
  }, [procedure, experimentalInputs, evaluatedValues, conditions, description, dataEntryMode]);
}

/**
 * Hook to produce an UpdateProcedureRunParams that can be used to provide a timing estimate.
 */
export function useProcedureRunUpdateParam(
  procedureRunId: string,
  conditions: Array<Condition>,
  experimentalInputs: ExperimentalInputs,
  evaluatedValues: ExperimentalInputEvaluatedValues
): UpdateProcedureRunParams {
  return useMemo(() => {
    // For simplicity, we serialize the full list of conditions and then take only the new conditions at the
    // end rather than modifying every input to the experiment plan to only include the new conditions
    const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
    const namesPerCondition = conditions.map((condition) => condition.name);
    const descriptionsPerCondition = conditions.map((condition) => condition.description);
    const experimentPlan = buildFullExperimentPlan({
      replicatesPerCondition,
      namesPerCondition,
      descriptionsPerCondition,
      numConditions: replicatesPerCondition.length,
      experimentalInputs,
      evaluatedValues,
    });
    const serializedConditions = serializeExperimentConditionsForNetwork(experimentPlan);
    const serializedNewConditions = serializedConditions.filter((c, index) => conditions[index].id == null);
    return {
      id: procedureRunId,
      newConditions: serializedNewConditions,
      confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
    };
  }, [procedureRunId, experimentalInputs, evaluatedValues, conditions]);
}

export const ARCHIVED_PLAN_ENTITIES_QUERY: DocumentNode = gql`
  query ArchivedPlanEntitiesQuery($bioentityIds: [StrictString!]!) @skipAudit @engComponent(name: STUDIES) {
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        id
        name
        archiveRecord {
          purpose
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ArchivedPlanEntitiesQueryData {
  bioentities: Array<{
    __typename: string;
    id?: string;
    name?: string;
    archiveRecord?: {purpose: string} | null;
  }>;
}

const EMPTY_ARCHIVED_ENTITY_IDS: ReadonlySet<string> = new Set();

/**
 * Collects unique bioentity IDs from experimental inputs (material inputs and equipment).
 * Returns a stable empty array when isPlan is false so the query is skipped.
 */
function useEntityIdsFromExperimentalInputs(
  experimentalInputs: ExperimentalInputs | null,
  isPlan: boolean
): Array<string> {
  return useMemo(() => {
    if (!isPlan || experimentalInputs == null) {
      return [];
    }
    const ids = Object.values(experimentalInputs)
      .flatMap((methodInputs) => methodInputs.values)
      .flatMap((conditionValues) => [
        ...conditionValues[ExperimentalInputsComponentName.MATERIAL_INPUTS],
        ...conditionValues[ExperimentalInputsComponentName.EQUIPMENT],
      ])
      .map((fields) => extractMaterialFromTypedValue(fields[0])?.id)
      .filter((id): id is string => id != null);
    return [...new Set(ids)];
  }, [experimentalInputs, isPlan]);
}

/**
 * Hook that queries for archived entities among the bioentity IDs referenced in experimental inputs.
 * Only runs when isPlan is true (archived entity validation is for plans only).
 */
function useArchivedEntityIds(
  experimentalInputs: ExperimentalInputs | null,
  isPlan: boolean
): ReadonlySet<string> {
  const entityIds = useEntityIdsFromExperimentalInputs(experimentalInputs, isPlan);

  const result = useSkippableBenchlingQuery<ArchivedPlanEntitiesQueryData>(ARCHIVED_PLAN_ENTITIES_QUERY, {
    variables: {bioentityIds: entityIds},
    skip: !isPlan || entityIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  return useMemo(() => {
    if (result.type !== QueryResultType.SUCCESS) {
      return EMPTY_ARCHIVED_ENTITY_IDS;
    }
    const archivedIds = result.data.bioentities
      .filter((bioentity) => !isInaccessible(bioentity) && bioentity.archiveRecord != null)
      .map((bioentity) => bioentity.id!);
    return archivedIds.length > 0 ? new Set(archivedIds) : EMPTY_ARCHIVED_ENTITY_IDS;
  }, [result]);
}

export function useExperimentalInputsContextProperties(
  conditions: Array<Condition> | null,
  experimentalInputs: ExperimentalInputs | null,
  setExperimentalInputs: ((inputs: ExperimentalInputs) => void) | null,
  flowchart: ExperimentFlowchart | null,
  procedureType: ProcedureType | null,
  isLocked: boolean = false,
  isPlan: boolean = false
): ExperimentalInputsContextProperties | null {
  // For plans, detect archived entities so cells referencing them show validation errors.
  const archivedEntityIds = useArchivedEntityIds(experimentalInputs, isPlan);

  // Create the table with isLocked=false for evaluation purposes for plans.
  // This ensures validation errors are computed and persist regardless of edit mode.
  // The actual locked state is used for UI locking via context.locked below.
  const fullTableMap = useMemo(
    () =>
      experimentalInputs != null && flowchart != null && procedureType != null && conditions != null
        ? convertExperimentalInputsToTableMap(
            experimentalInputs,
            flowchart,
            procedureType,
            isPlan ? false : isLocked,
            conditions,
            isPlan,
            archivedEntityIds
          )
        : {},
    [experimentalInputs, flowchart, conditions, procedureType, isPlan, isLocked, archivedEntityIds]
  );
  const simpleTableDoc = useSimpleEvaluation(fullTableMap);
  if (setExperimentalInputs == null) {
    assert(isLocked, 'setExperimentalInputs should only be null for locked editor');
  }

  const evaluatedValues = useMemo(
    () =>
      experimentalInputs != null && flowchart != null && conditions != null
        ? convertOutputTableMapToEvaluatedValues(
            simpleTableDoc.noteRuntimeSnapshot.evaluationSnapshot,
            experimentalInputs
          )
        : null,
    [simpleTableDoc.noteRuntimeSnapshot.evaluationSnapshot, experimentalInputs, flowchart, conditions]
  );

  return useMemo(
    () =>
      experimentalInputs != null && flowchart != null && conditions != null && evaluatedValues != null
        ? {
            numConditions: conditions.length,
            replicatesPerCondition: conditions.map((condition) => condition.numReplicates),
            namesPerCondition: conditions.map((condition) => condition.name),
            descriptionsPerCondition: conditions.map((condition) => condition.description),
            numReplicates: conditions.map((condition) => condition.numReplicates)[0],
            draftInputs: experimentalInputs,
            setExperimentalInputs: (methodId, newInputs, newConfirmationMethods) => {
              assert(setExperimentalInputs != null, 'Unexpected edit on locked design.');
              assert(experimentalInputs != null, 'Experimental inputs must be set before continuing');
              assert(experimentalInputs.hasOwnProperty(methodId), `Unknown methodId: ${methodId}`);
              setExperimentalInputs({
                ...experimentalInputs,
                [methodId]: {
                  ...experimentalInputs[methodId],
                  values: newInputs,
                  parameterConfirmationMethods: newConfirmationMethods,
                },
              });
            },
            locked: isLocked,
            simpleTableDoc,
            evaluatedValues,
          }
        : null,
    [
      conditions,
      experimentalInputs,
      setExperimentalInputs,
      evaluatedValues,
      flowchart,
      isLocked,
      simpleTableDoc,
    ]
  );
}

/**
 * Hook to manage the state for designing a procedure-based experiment.
 *
 * @returns a {@link ExperimentDesignState} object.
 */
export function useExperimentDesignState(
  initialValues: CreateStudyModalOptionalParams,
  /**
   * The default data entry mode. Note: this is intentionally not exposed as part of {@link initialValues}
   * as it should only be configured on a per-study-type basis.
   */
  initialDataEntryMode: ProcedureRunDataEntryMode
): ExperimentDesignState {
  const [procedureId, setProcedureId] = useState<string | null>(initialValues.initialProcedureId ?? null);
  const [description, setDescription] = useState<string>('');
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(initialDataEntryMode);
  const {procedure} = useExperimentProcedure(procedureId ? {procedureId} : null);
  const [selectedMethodId, setSelectedMethodId] = useSelectedMethodId(procedure);
  const {conditions, setConditions, experimentalInputs, setExperimentalInputs} =
    useConditionsAndExperimentalInputs(procedure, createDefaultRunConditions);
  const context = useExperimentalInputsContextProperties(
    conditions,
    experimentalInputs,
    setExperimentalInputs,
    procedure?.procedureFlowchart ?? null,
    procedure?.procedureType ?? null
  );
  const methodExecutionLimitState = useMethodExecutionLimitState(
    experimentalInputs,
    conditions,
    procedure?.largestTableTypeSizes ?? null,
    dataEntryMode
  );
  const experimentParam = useExperimentParam(
    procedure,
    conditions,
    description,
    experimentalInputs,
    context?.evaluatedValues ?? null,
    dataEntryMode
  );

  return {
    procedure,
    procedureId,
    setProcedureId,
    selectedMethodId,
    setSelectedMethodId,
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    context,
    methodExecutionLimitState,
    experimentParam,
    description,
    setDescription,
    dataEntryMode,
    setDataEntryMode,
  };
}

/**
 * These two pieces of state are managed together as they are closely related.
 *
 * Behaviors:
 *   - Whenever `procedure` is loading, `experimentalInputs` will be `null`.
 *   - Whenever `procedure` has loaded, `experimentalInputs` will be initialized to the initial values.
 *   - Whenever `numConditions` is updated, `experimentalInputs` will be re-initialized preserving values
 *     when possible.
 */
export function useConditionsAndExperimentalInputs<TCondition extends BaseCondition = Condition>(
  procedure: Pick<ExperimentProcedure, 'id' | 'procedureType' | 'methods'> | null,
  /**
   * Factory for the fallback default conditions used when there are no stored conditions for the
   * current procedure (e.g. on first load, or when the procedure changes). Required so that callers
   * with non-default `TCondition` (e.g. {@link ConditionGroup}) can produce a value of the correct
   * shape. See {@link createDefaultRunConditions} and
   * {@link createDefaultPlanConditions} for the standard run/plan factories.
   */
  createDefaultConditions: (procedureType: ProcedureType) => Array<TCondition>,
  // Initial values are non-null when editing an existing experiment design
  initialProcedureId: string | null = null,
  initialConditions: Array<TCondition> | null = null,
  initialExperimentalInputs: ExperimentalInputs | null = null
): {
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: Dispatch<SetStateAction<ExperimentalInputs | null>>;
  conditions: Array<TCondition> | null;
  /**
   * @param conditions - new conditions
   * @param prevIndexes - for the condition, which index in ExperimentalInputs its values can be found.
   *    null if the condition does not have value data yet.
   */
  setConditions: (conditions: Array<TCondition> | null, prevIndexes: Array<number | null>) => void;
  /**
   * Atomically reset both conditions and experimental inputs to provided values.
   * Used when canceling edits to restore the original state without stale closure issues.
   */
  resetConditionsAndInputs: (conditions: Array<TCondition>, experimentalInputs: ExperimentalInputs) => void;
} {
  const [data, setData] = useState<{
    conditions: Array<TCondition> | null;
    experimentalInputs: ExperimentalInputs | null;
    /** procedureId used to generate the experimentalInputs. */
    procedureId: string | null;
  }>({
    conditions: initialConditions,
    experimentalInputs: initialExperimentalInputs,
    procedureId: initialProcedureId,
  });

  // Maintain invariant that experimentalInputs properly reflects procedure and numConditions.
  const {
    conditions: storedConditions,
    experimentalInputs: storedExperimentalInputs,
    procedureId: storedProcedureId,
  } = data;
  let conditions: Array<TCondition> | null;
  let experimentalInputs: ExperimentalInputs | null;
  let procedureId: string | null;
  if (procedure == null) {
    conditions = null;
    experimentalInputs = null;
    procedureId = null;
  } else {
    conditions =
      storedConditions != null && procedure.id === storedProcedureId
        ? storedConditions
        : createDefaultConditions(procedure.procedureType);
    experimentalInputs =
      storedExperimentalInputs != null && procedure.id === storedProcedureId
        ? storedExperimentalInputs
        : prepareInitialExperimentalInputs(conditions.length, procedure.methods);
    procedureId = procedure.id;
  }

  if (
    conditions !== storedConditions ||
    experimentalInputs !== storedExperimentalInputs ||
    procedureId !== storedProcedureId
  ) {
    // Store in state so that we can allow user to iterate from the initial state.
    // NOTE: While setting state on render has the potential to cause infinite render loops, we do not expect
    // this to happen as the next experimentalInputs is expected to match the new storedExperimentalInputs.
    setData({...data, conditions, experimentalInputs, procedureId});
  }

  const setConditions = useCallback(
    (conditions: Array<TCondition>, prevIndexes: Array<number | null>): void => {
      setData({
        ...data,
        conditions,
        experimentalInputs:
          procedure && experimentalInputs
            ? prepareInitialExperimentalInputs(conditions.length, procedure.methods, {
                prevExperimentalInputValues: mapObject(
                  experimentalInputs,
                  (inputsPerMethod: ExperimentalInputsPerMethod) => ({
                    // When columns are added/removed, make sure values shift accordingly.
                    values: prevIndexes.map((index) =>
                      index != null ? inputsPerMethod.values[index] : inputsPerMethod.defaultValues
                    ),
                  })
                ),
                // Preserve confirmation methods since they apply across all conditions
                prevConfirmationMethods: experimentalInputs,
              })
            : null,
      });
    },
    [data, experimentalInputs, procedure]
  );

  const setExperimentalInputs = useCallback(
    (newExperimentalInputs: ExperimentalInputs) => {
      assert(procedure != null, 'Procedure must be loaded before setting experimental inputs.');
      setData({...data, experimentalInputs: newExperimentalInputs, procedureId: procedure.id});
    },
    [data, procedure]
  );

  const resetConditionsAndInputs = useCallback(
    (newConditions: Array<TCondition>, newExperimentalInputs: ExperimentalInputs) => {
      assert(procedure != null, 'Procedure must be loaded before resetting conditions and inputs.');
      setData({
        conditions: newConditions,
        experimentalInputs: newExperimentalInputs,
        procedureId: procedure.id,
      });
    },
    [procedure]
  );

  return {conditions, setConditions, experimentalInputs, setExperimentalInputs, resetConditionsAndInputs};
}

/**
 * Hook to manage the selected methodId of a loaded procedure.
 *
 * @returns `[selectedMethodId, setSelectedMethodId]`
 *  - `selectedMethodId` as defined in {@link ExperimentDesignState.selectedMethodId}.
 *  - `setSelectedMethodId` as defined in {@link ExperimentDesignState.setSelectedMethodId}.
 */
function useSelectedMethodId(
  procedure: ExperimentProcedure | null
): [string | null, Dispatch<SetStateAction<string | null>>] {
  const [storedMethodId, setSelectedMethodId] = useState<string | null>(null);
  let selectedMethodId: string | null;
  if (procedure == null) {
    // No procedure is loaded, so no method should be selected yet.
    selectedMethodId = null;
  } else if (storedMethodId == null || !procedure.methods.some((m) => m.id === storedMethodId)) {
    // Selected method is incomptible with the loaded procedure, reset to the first.
    selectedMethodId = procedure.methods[0]?.id ?? null;
  } else {
    // Selected method is compatible with the loaded procedure.
    selectedMethodId = storedMethodId;
  }
  if (selectedMethodId !== storedMethodId) {
    // Store in state so that we can allow user to iterate from the initial state.
    // NOTE: While setting state on render has the potential to cause infinite render loops, we do not expect
    // this to happen as the next selectedMethodId is expected to match the new storedMethodId.
    setSelectedMethodId(selectedMethodId);
  }
  return [selectedMethodId, setSelectedMethodId];
}
